import { useState } from "react";
import { useCustomMutation } from "data/modules/api/hooks/useCustomMutation";
import { useCustomQuery } from "data/modules/api/hooks/useCustomQuery";
import {
  PAYMENT_LINK_APIS,
  FETCH_PAYMENT_LINK,
} from "features/PaymentLinks/constants/PaymentLinks.apis";
import { listing_apis } from "features/Listings/Listings.constants";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { useRefresh } from "react-admin";
import {
  getBillingStateName,
  getQrOrderUuid,
  buildQrListingMapPayload,
} from "../utils/qrPayments.utils";
import {
  QR_QUERY_KEYS,
  DEFAULT_COUNTRY_CODE,
  QR_CACHE_TIME,
  QR_DETAILS_LINK_FIELD,
  LINK_NOT_AVAILABLE_MSG,
} from "../constants/qrPayments.constants";
import { copyToClipboard } from "features/Common/utils/Copy.utils";
import { logError } from "utils/error";

export const useQrPayments = () => {
  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const { pushNotification, notify } = useNotifications();
  const refetch = useRefresh();

  const { mutateAsync } = useCustomMutation({
    url: PAYMENT_LINK_APIS.attach_customer,
  });

  const dynamicLinkId = selectedRecord?.dynamic_link_id;
  const isFetchEnabled = isAddCustomerOpen && Boolean(dynamicLinkId);

  // Query dynamic link details to get abbreviated listing record (specifically listing UUID)
  const { data: dynamicLinkData, isLoading: isDynamicLinkLoading } =
    useCustomQuery({
      url: `${FETCH_PAYMENT_LINK}/${dynamicLinkId}`,
      queryKey: dynamicLinkId
        ? [QR_QUERY_KEYS.DYNAMIC_LINK_DETAILS, dynamicLinkId]
        : [QR_QUERY_KEYS.DYNAMIC_LINK_DETAILS],
      params: {},
      enabled: isFetchEnabled,
      cacheTime: QR_CACHE_TIME,
    });

  const listingUuid = isFetchEnabled
    ? dynamicLinkData?.listings?.[0]?.uuid ||
      dynamicLinkData?.listings?.[0]?.listing_uuid
    : null;

  // Query full details of the listing (batches, timing, pricing, availability)
  const { data: fullListingDetails, isLoading: isListingDetailsLoading } =
    useCustomQuery({
      url: `${listing_apis.get_listing_details}/${listingUuid}`,
      queryKey: listingUuid
        ? [QR_QUERY_KEYS.LISTING_DETAILS, listingUuid]
        : [QR_QUERY_KEYS.LISTING_DETAILS],
      params: {},
      enabled: isFetchEnabled && Boolean(listingUuid),
      cacheTime: QR_CACHE_TIME,
    });

  const isLinkLoading =
    isFetchEnabled && (isDynamicLinkLoading || isListingDetailsLoading);
  const resolvedListingData =
    isFetchEnabled && !isLinkLoading ? fullListingDetails?.event || null : null;

  const handleAddCustomer = (record) => {
    setSelectedRecord(record);
    setIsAddCustomerOpen(true);
  };

  const handleSendThankYouLink = (record) => {
    const link =
      record?.[QR_DETAILS_LINK_FIELD] ||
      selectedRecord?.[QR_DETAILS_LINK_FIELD] ||
      "";
    if (!link) {
      notify(LINK_NOT_AVAILABLE_MSG, notification_color_keys.error);
      return;
    }
    copyToClipboard(link, notify);
  };

  const handleCloseModal = () => {
    setIsAddCustomerOpen(false);
    setSelectedRecord(null);
  };

  const handleAttachCustomerSubmit = async (values) => {
    try {
      const stateName = getBillingStateName(values);
      const qrOrderUuid = getQrOrderUuid(selectedRecord);
      const { listingUuid: finalListingUuid, listingMapPayload } =
        buildQrListingMapPayload(values, resolvedListingData);

      await mutateAsync({
        qr_order_uuid: qrOrderUuid,
        full_name: values.full_name,
        email: values.email,
        phone: values.phone || values.customer_phone_number,
        country_code: values.country_code || DEFAULT_COUNTRY_CODE,
        state: stateName,
        ...(finalListingUuid && { listing_uuid: finalListingUuid }),
        ...(listingMapPayload && { listing_map: listingMapPayload }),
      });

      pushNotification("Customer details added successfully!", {
        variant: "outlined",
        color: "primary",
      });
      handleCloseModal();
      refetch();
    } catch (err) {
      console.error("Error attaching customer details:", err);
      logError({
        err,
        when: "submit customer details for QR payment",
        occuredAt:
          "src/ui/pages/transactions/QrPayments/hooks/useQrPayments.js",
      });
    }
  };

  return {
    isAddCustomerOpen,
    selectedRecord,
    handleAddCustomer,
    handleCloseModal,
    handleSendThankYouLink,
    handleAttachCustomerSubmit,
    listingData: resolvedListingData,
    isLinkLoading,
  };
};
